import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HIPAA safeguard: capture explicit consent for data handling before the user
 * works with care features. Shows once until accepted (per consent version).
 */
export const ConsentGate = () => {
  const { user, getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    let active = true;
    axios.get(`${API}/consent`, getAuthHeader())
      .then((r) => { if (active && !r.data.accepted) setOpen(true); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const accept = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/consent`, { accepted: true }, getAuthHeader());
      setOpen(false);
      // Notify other modals (OnboardingConcierge) that consent is now
      // accepted so they can safely take focus. Without this signal the
      // OnboardingConcierge would either (a) stay closed until the next
      // page load or (b) stack on top of us — but Radix Dialog sets
      // `pointer-events:none` on <body> while open, which kills clicks on
      // any sibling modal. See Feb 2026 bug: the OnboardingConcierge X
      // button was un-clickable for every fresh patient because we were
      // covering it. Now we ALWAYS run alone, then hand off cleanly.
      try {
        window.dispatchEvent(new Event('caraloom:consent-accepted'));
      } catch (_) { /* not all envs support CustomEvent — best effort */ }
    } catch (e) {
      // keep open on failure
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* require a choice */ }}>
      <DialogContent
        className="max-w-lg"
        data-testid="consent-gate"
        hideClose
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center mb-2">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <DialogTitle className="font-display text-2xl text-cn-text">Your privacy & consent</DialogTitle>
          <DialogDescription className="text-cn-text-muted">
            Before you continue, please review how CaraLoom handles your information.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-3 text-sm text-cn-text">
          <div className="flex items-start gap-2">
            <Lock className="w-4 h-4 text-cn-primary mt-0.5 flex-shrink-0" />
            <p>We collect only what's needed to match you with the right care, and protect it with encryption, role-based access, and an audit trail of who views your data.</p>
          </div>
          <div className="flex items-start gap-2">
            <Lock className="w-4 h-4 text-cn-primary mt-0.5 flex-shrink-0" />
            <p>CaraLoom is a care-coordination software platform, not your medical provider. AI features are informational and every consequential action is reviewed by a licensed clinician — never a substitute for medical advice.</p>
          </div>
          <div className="flex items-start gap-2">
            <Lock className="w-4 h-4 text-cn-primary mt-0.5 flex-shrink-0" />
            <p>You can view, export, or delete your data anytime from your <Link to="/privacy/dashboard" className="text-cn-primary font-semibold underline">Privacy Dashboard</Link>.</p>
          </div>
        </div>
        <label className="mt-4 flex items-start gap-3 p-3 rounded-2xl bg-cn-bg/60 border border-cn-border/50 cursor-pointer">
          <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="consent-checkbox" className="mt-0.5" />
          <span className="text-sm text-cn-text">
            I understand and agree to CaraLoom's <Link to="/privacy" className="text-cn-primary underline">Privacy Policy</Link> and <Link to="/terms" className="text-cn-primary underline">Terms</Link>, including how my information is used and protected.
          </span>
        </label>
        <Button
          onClick={accept}
          disabled={!agreed || submitting}
          className="cn-btn cn-btn-primary w-full mt-4 !py-3 disabled:opacity-40"
          data-testid="consent-accept"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Agree & continue'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentGate;
